import React, { useMemo } from 'react';
import classNames from 'classnames';
import i18n from 'i18n';
import { CreativeType } from 'core/creative/Creative';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { Slider } from 'components/common/Slider/Slider';
import { DefaultSliderModel } from 'components/common/Slider/SliderModel';
import styles from './archivedCreative.module.scss';

const WIDE_ARCHIVED_PREVIEW_TYPES = [
  CreativeType.VIDEO,
  CreativeType.EDIMAX,
  CreativeType.PIC_SHORT,
  CreativeType.PIC_LONG,
  CreativeType.PIC_EXTENDED,
  CreativeType.PIC_BROAD,
  CreativeType.FRIDGE_LONG,
  CreativeType.FRIDGE_EXTENDED,
  CreativeType.FRIDGE_BROAD
];

const isDynamicCreativeType = (creativeType: CreativeType) => {
  const channelGroupId =
    ChannelGroupSetting.findChannelGroupIdByCreativeType(creativeType);
  return (
    channelGroupId !== undefined &&
    !ChannelGroupSetting.isStaticChannelGroup(channelGroupId)
  );
};

/**
 * 已封存素材預覽灰色色塊的長寬比，依 PM 規格：原生 7:3、影片與戶外（含動態渠道素材類型）16:9，其餘 6:5。
 * @param creativeType 素材類型
 * @returns CSS aspect-ratio 值
 */
export const getArchivedPreviewAspectRatio = (creativeType: CreativeType) => {
  if (creativeType === CreativeType.NATIVE) {
    return '7 / 3';
  }
  return WIDE_ARCHIVED_PREVIEW_TYPES.includes(creativeType) ||
    isDynamicCreativeType(creativeType)
    ? '16 / 9'
    : '6 / 5';
};

const NATIVE_SLIDER_WIDTH = 150;
const NATIVE_SLIDER_HEIGHT = 115;

const ArchivedNativePreview: React.FC = () => {
  const sliderModel = useMemo(
    () =>
      new DefaultSliderModel(
        NATIVE_SLIDER_WIDTH,
        NATIVE_SLIDER_HEIGHT,
        true,
        false,
        0,
        true
      ),
    []
  );
  return (
    <Slider model={sliderModel}>
      <div
        className={styles.archivedPreview}
        style={{
          aspectRatio: getArchivedPreviewAspectRatio(CreativeType.NATIVE)
        }}
      />
      <div className={classNames(styles.archivedPreview, styles.square)} />
    </Slider>
  );
};

/**
 * 已封存素材的預覽灰色色塊；原生素材比照原本的預覽，用 slider 切換大圖（7:3）與 icon（1:1）兩塊。
 * @param creativeType 素材類型，決定色塊比例
 */
export const ArchivedPreview: React.FC<{ creativeType: CreativeType }> = ({
  creativeType
}) =>
  creativeType === CreativeType.NATIVE ? (
    <ArchivedNativePreview />
  ) : (
    <div
      className={styles.archivedPreview}
      style={{ aspectRatio: getArchivedPreviewAspectRatio(creativeType) }}
    />
  );

/**
 * 已封存素材名稱下方的封存提示。
 * @param className 額外的 class，列表用來標記保留原色
 */
export const ArchivedNotice: React.FC<{ className?: string }> = ({
  className
}) => (
  <div className={classNames(styles.archivedNotice, className)}>
    {i18n.t('creativeList.labels.archivedNotice')}
  </div>
);
